(function () {
  var instances = new WeakMap();
  var instancesBySectionId = {};
  var youtubeApiPromise = null;
  var vimeoApiPromise = null;

  var isSectionAlive = function (section, initToken) {
    return Boolean(
      initToken &&
        initToken.alive &&
        section &&
        section.isConnected
    );
  };

  var clearExternalVideoContainer = function (container) {
    if (!container) {
      return;
    }

    container.innerHTML = '';
  };

  var getSectionRoot = function (scope) {
    if (!scope) {
      return document;
    }

    if (scope.matches && scope.matches('[data-custom-half-video]')) {
      return scope;
    }

    var sectionRoot = scope.querySelector('[data-custom-half-video]');

    return sectionRoot || scope;
  };

  var containsHalfVideo = function (scope) {
    if (!scope) {
      return false;
    }

    if (scope.matches && scope.matches('[data-custom-half-video]')) {
      return true;
    }

    return Boolean(scope.querySelector('[data-custom-half-video]'));
  };

  var loadYouTubeAPI = function () {
    if (youtubeApiPromise) {
      return youtubeApiPromise;
    }

    if (window.YT && window.YT.loaded) {
      youtubeApiPromise = Promise.resolve();
      return youtubeApiPromise;
    }

    youtubeApiPromise = new Promise(function (resolve) {
      window.onYouTubeIframeAPIReady = function () {
        resolve();
      };

      var tag = document.createElement('script');
      tag.src = 'https://www.youtube.com/iframe_api';
      document.body.appendChild(tag);
    });

    return youtubeApiPromise;
  };

  var loadVimeoAPI = function () {
    if (vimeoApiPromise) {
      return vimeoApiPromise;
    }

    if (window.Vimeo) {
      vimeoApiPromise = Promise.resolve();
      return vimeoApiPromise;
    }

    vimeoApiPromise = new Promise(function (resolve) {
      var tag = document.createElement('script');
      tag.src = 'https://player.vimeo.com/api/player.js';
      tag.onload = resolve;
      document.body.appendChild(tag);
    });

    return vimeoApiPromise;
  };

  var setPlayingState = function (section, isPlaying) {
    var toggleButton = section.querySelector('[data-custom-half-video-toggle]');

    if (!toggleButton) {
      return;
    }

    toggleButton.setAttribute('aria-pressed', isPlaying ? 'true' : 'false');
    toggleButton.setAttribute(
      'aria-label',
      isPlaying ? 'Pause video' : 'Play video'
    );
  };

  var setMutedState = function (section, isMuted) {
    var muteButton = section.querySelector('[data-custom-half-video-mute]');

    if (!muteButton) {
      return;
    }

    muteButton.setAttribute('aria-pressed', isMuted ? 'false' : 'true');
    muteButton.setAttribute(
      'aria-label',
      isMuted ? 'Unmute video' : 'Mute video'
    );
  };

  var hidePoster = function (section) {
    var poster = section.querySelector('.custom-half-video__poster');

    if (poster) {
      poster.classList.add('is-hidden');
    }
  };

  var initShopifyVideo = function (section, video) {
    var play = function () {
      var promise = video.play();

      if (promise && typeof promise.catch === 'function') {
        promise.catch(function () {});
      }
    };

    play();
    setPlayingState(section, !video.paused);
    setMutedState(section, video.muted);

    return {
      play: play,
      pause: function () {
        video.pause();
      },
      isPlaying: function () {
        return !video.paused;
      },
      mute: function () {
        video.muted = true;
      },
      unmute: function () {
        video.muted = false;
      },
      isMuted: function () {
        return video.muted;
      },
      destroy: function () {},
    };
  };

  var initYouTubeVideo = function (section, container, videoId, initToken, registerPlayer) {
    clearExternalVideoContainer(container);

    return loadYouTubeAPI().then(function () {
      if (!isSectionAlive(section, initToken)) {
        return null;
      }

      return new Promise(function (resolve) {
        var player = new window.YT.Player(container, {
          videoId: videoId,
          playerVars: {
            autoplay: 1,
            controls: 0,
            mute: 1,
            loop: 1,
            playlist: videoId,
            playsinline: 1,
            modestbranding: 1,
            rel: 0,
            iv_load_policy: 3,
            disablekb: 1,
          },
          events: {
            onReady: function () {
              if (!isSectionAlive(section, initToken)) {
                try {
                  player.destroy();
                } catch (error) {}

                resolve(null);
                return;
              }

              hidePoster(section);
              setPlayingState(section, true);
              setMutedState(section, true);
              resolve({
                play: function () {
                  player.playVideo();
                },
                pause: function () {
                  player.pauseVideo();
                },
                isPlaying: function () {
                  return player.getPlayerState() === window.YT.PlayerState.PLAYING;
                },
                mute: function () {
                  player.mute();
                },
                unmute: function () {
                  player.unMute();
                },
                isMuted: function () {
                  return player.isMuted();
                },
                destroy: function () {
                  try {
                    player.destroy();
                  } catch (error) {}

                  clearExternalVideoContainer(container);
                },
              });
            },
          },
        });

        if (typeof registerPlayer === 'function') {
          registerPlayer(player);
        }
      });
    });
  };

  var initVimeoVideo = function (section, container, videoId, initToken, registerPlayer) {
    clearExternalVideoContainer(container);

    return loadVimeoAPI().then(function () {
      if (!isSectionAlive(section, initToken)) {
        return null;
      }

      var player = new window.Vimeo.Player(container, {
        id: videoId,
        autoplay: true,
        loop: true,
        muted: true,
        controls: false,
        background: true,
      });

      if (typeof registerPlayer === 'function') {
        registerPlayer(player);
      }

      if (!isSectionAlive(section, initToken)) {
        player.destroy().finally(function () {
          clearExternalVideoContainer(container);
        });
        return null;
      }

      hidePoster(section);
      setPlayingState(section, true);
      setMutedState(section, true);

      return {
        play: function () {
          player.play();
        },
        pause: function () {
          player.pause();
        },
        isPlaying: function () {
          return player.getPaused().then(function (paused) {
            return !paused;
          });
        },
        mute: function () {
          return player.setMuted(true);
        },
        unmute: function () {
          return player.setMuted(false);
        },
        isMuted: function () {
          return player.getMuted();
        },
        destroy: function () {
          player.destroy().finally(function () {
            clearExternalVideoContainer(container);
          });
        },
      };
    });
  };

  var initExternalVideo = function (section, container, initToken, registerPlayer) {
    var provider = container.dataset.videoProvider;
    var videoId = container.dataset.videoId;

    if (provider === 'youtube') {
      return initYouTubeVideo(section, container, videoId, initToken, registerPlayer);
    }

    if (provider === 'vimeo') {
      return initVimeoVideo(section, container, videoId, initToken, registerPlayer);
    }

    return Promise.resolve(null);
  };

  var EXPAND_DURATION = 700;

  var getHeaderHeight = function () {
    return (
      parseFloat(
        getComputedStyle(document.documentElement).getPropertyValue('--header-height')
      ) || 0
    );
  };

  var getViewportHeight = function () {
    var vhUnit = parseFloat(
      getComputedStyle(document.documentElement).getPropertyValue('--vh')
    );

    if (vhUnit) {
      return vhUnit * 100;
    }

    return window.innerHeight;
  };

  var scrollVideoToViewportCenter = function (section) {
    var shell = section.querySelector('[data-custom-half-video-shell]');

    if (!shell) {
      return;
    }

    var prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    var scrollBehavior = prefersReducedMotion ? 'auto' : 'smooth';

    if (window.matchMedia('(max-width: 959px)').matches) {
      shell.scrollIntoView({
        behavior: scrollBehavior,
        block: 'nearest',
      });
      return;
    }

    var scroll = function () {
      var headerHeight = getHeaderHeight();
      var visibleHeight = getViewportHeight() - headerHeight;
      var rect = shell.getBoundingClientRect();
      var targetCenter = rect.top + rect.height / 2;
      var desiredCenter = headerHeight + visibleHeight / 2;
      var nextScrollTop = window.scrollY + (targetCenter - desiredCenter);

      window.scrollTo({
        top: Math.max(0, nextScrollTop),
        behavior: scrollBehavior,
      });
    };

    window.requestAnimationFrame(function () {
      window.requestAnimationFrame(scroll);
    });
  };

  var ensureMuted = function (section, activeController) {
    if (!activeController || typeof activeController.mute !== 'function') {
      return;
    }

    activeController.mute();
    setMutedState(section, true);
  };

  var setExpandedState = function (section, isExpanded) {
    var expandButton = section.querySelector('[data-custom-half-video-expand]');
    var muteButton = section.querySelector('[data-custom-half-video-mute]');

    section.classList.toggle('is-expanded', isExpanded);

    if (expandButton) {
      expandButton.setAttribute('aria-expanded', isExpanded ? 'true' : 'false');
      expandButton.setAttribute(
        'aria-label',
        isExpanded ? 'Collapse video' : 'Expand video'
      );
    }

    if (muteButton) {
      muteButton.hidden = !isExpanded;
      muteButton.tabIndex = isExpanded ? 0 : -1;
    }
  };

  var applyExpandedState = function (section, controllerPromise, isExpanded) {
    setExpandedState(section, isExpanded);

    if (!isExpanded) {
      controllerPromise.then(function (activeController) {
        ensureMuted(section, activeController);
      });
    }
  };

  var init = function (section) {
    var sectionId = section.dataset.sectionId;
    var initToken = { alive: true };
    var videoType = section.dataset.videoType;
    var nativeVideo = section.querySelector('[data-custom-half-video-native]');
    var externalVideo = section.querySelector('[data-custom-half-video-external]');
    var toggleButton = section.querySelector('[data-custom-half-video-toggle]');
    var expandButton = section.querySelector('[data-custom-half-video-expand]');
    var muteButton = section.querySelector('[data-custom-half-video-mute]');
    var controllerPromise = Promise.resolve(null);
    var activeController = null;
    var pendingPlayer = null;
    var listeners = [];
    var isAnimating = false;
    var destroyed = false;

    if (sectionId && instancesBySectionId[sectionId]) {
      instancesBySectionId[sectionId].destroy();
    }

    section.classList.remove('is-expanded');
    setExpandedState(section, false);

    var registerPlayer = function (player) {
      pendingPlayer = player;
    };

    var destroyController = function (controller) {
      if (!controller || typeof controller.destroy !== 'function') {
        return;
      }

      controller.destroy();
    };

    if (videoType === 'shopify' && nativeVideo) {
      activeController = initShopifyVideo(section, nativeVideo);
      controllerPromise = Promise.resolve(activeController);
    } else if (externalVideo) {
      controllerPromise = initExternalVideo(
        section,
        externalVideo,
        initToken,
        registerPlayer
      ).then(function (controller) {
        if (destroyed) {
          destroyController(controller);
          return null;
        }

        activeController = controller;
        pendingPlayer = null;
        return controller;
      });
    }

    if (toggleButton) {
      var onToggle = function () {
        controllerPromise.then(function (activeController) {
          if (!activeController || typeof activeController.isPlaying !== 'function') {
            return;
          }

          var applyToggle = function (isPlaying) {
            if (isPlaying) {
              activeController.pause();
              setPlayingState(section, false);
              return;
            }

            activeController.play();
            setPlayingState(section, true);
          };

          var playingState = activeController.isPlaying();

          if (playingState && typeof playingState.then === 'function') {
            playingState.then(applyToggle);
            return;
          }

          applyToggle(playingState);
        });
      };

      toggleButton.addEventListener('click', onToggle);
      listeners.push(function () {
        toggleButton.removeEventListener('click', onToggle);
      });
    }

    if (muteButton) {
      setMutedState(section, true);

      var onMute = function () {
        controllerPromise.then(function (activeController) {
          if (!activeController || typeof activeController.isMuted !== 'function') {
            return;
          }

          var applyToggle = function (isMuted) {
            if (isMuted) {
              activeController.unmute();
              setMutedState(section, false);
              return;
            }

            activeController.mute();
            setMutedState(section, true);
          };

          var mutedState = activeController.isMuted();

          if (mutedState && typeof mutedState.then === 'function') {
            mutedState.then(applyToggle);
            return;
          }

          applyToggle(mutedState);
        });
      };

      muteButton.addEventListener('click', onMute);
      listeners.push(function () {
        muteButton.removeEventListener('click', onMute);
      });
    }

    if (expandButton) {
      var onExpand = function () {
        if (isAnimating) {
          return;
        }

        isAnimating = true;
        var nextExpanded = !section.classList.contains('is-expanded');
        applyExpandedState(section, controllerPromise, nextExpanded);

        if (nextExpanded) {
          scrollVideoToViewportCenter(section);

          window.setTimeout(function () {
            scrollVideoToViewportCenter(section);
          }, EXPAND_DURATION);
        }

        window.setTimeout(function () {
          isAnimating = false;
        }, EXPAND_DURATION);
      };

      expandButton.addEventListener('click', onExpand);
      listeners.push(function () {
        expandButton.removeEventListener('click', onExpand);
      });
    }

    var onKeydown = function (event) {
      if (event.key !== 'Escape' || !section.classList.contains('is-expanded') || isAnimating) {
        return;
      }

      isAnimating = true;
      applyExpandedState(section, controllerPromise, false);

      window.setTimeout(function () {
        isAnimating = false;
      }, EXPAND_DURATION);
    };

    document.addEventListener('keydown', onKeydown);
    listeners.push(function () {
      document.removeEventListener('keydown', onKeydown);
    });

    var instance = {
      destroy: function () {
        if (destroyed) {
          return;
        }

        destroyed = true;
        initToken.alive = false;
        isAnimating = false;

        if (section.isConnected) {
          applyExpandedState(section, controllerPromise, false);
        } else {
          setExpandedState(section, false);
        }

        destroyController(activeController);
        activeController = null;

        controllerPromise.then(function (controller) {
          if (controller && controller !== activeController) {
            destroyController(controller);
          }
        });

        if (pendingPlayer) {
          try {
            if (typeof pendingPlayer.destroy === 'function') {
              pendingPlayer.destroy();
            } else if (typeof pendingPlayer.destroy === 'undefined' && pendingPlayer.pause) {
              pendingPlayer.pause();
            }
          } catch (error) {}

          pendingPlayer = null;
        }

        clearExternalVideoContainer(externalVideo);

        listeners.forEach(function (unsubscribe) {
          unsubscribe();
        });

        instances.delete(section);

        if (sectionId && instancesBySectionId[sectionId] === instance) {
          delete instancesBySectionId[sectionId];
        }
      },
    };

    if (sectionId) {
      instancesBySectionId[sectionId] = instance;
    }

    instances.set(section, instance);

    return instance;
  };

  var destroy = function (section) {
    var instance = instances.get(section);

    if (instance) {
      instance.destroy();
      return;
    }

    var sectionId = section && section.dataset ? section.dataset.sectionId : null;

    if (sectionId && instancesBySectionId[sectionId]) {
      instancesBySectionId[sectionId].destroy();
    }
  };

  var destroyBySectionId = function (sectionId) {
    if (!sectionId || !instancesBySectionId[sectionId]) {
      return;
    }

    instancesBySectionId[sectionId].destroy();
  };

  var initAll = function (scope) {
    var container = getSectionRoot(scope);

    container.querySelectorAll('[data-custom-half-video]').forEach(function (section) {
      destroy(section);
      init(section);
    });
  };

  var destroyAll = function (scope) {
    var container = getSectionRoot(scope);

    container.querySelectorAll('[data-custom-half-video]').forEach(function (section) {
      destroy(section);
    });
  };

  var handleSectionLoad = function (event) {
    var scope = event.target;

    if (!containsHalfVideo(scope)) {
      return;
    }

    if (event.detail && event.detail.sectionId) {
      destroyBySectionId(String(event.detail.sectionId));
    }

    initAll(scope);
  };

  var handleSectionUnload = function (event) {
    var scope = event.target;

    if (!containsHalfVideo(scope)) {
      return;
    }

    destroyAll(scope);

    if (event.detail && event.detail.sectionId) {
      destroyBySectionId(String(event.detail.sectionId));
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', handleSectionLoad);
  document.addEventListener('shopify:section:unload', handleSectionUnload);
  document.addEventListener('shopify:section:reorder', function (event) {
    if (!containsHalfVideo(event.target)) {
      return;
    }

    initAll(event.target);
  });
})();
